import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Grid,
  Paper,
  Alert,
  Typography,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import WebhookIcon from '@mui/icons-material/Webhook';
import LinkIcon from '@mui/icons-material/Link';
import KeyIcon from '@mui/icons-material/Key';
import TagIcon from '@mui/icons-material/Tag';
import PhoneIcon from '@mui/icons-material/Phone';
import { UpgradePrompt } from '../ui/UpgradePrompt';
import { useAuth } from '../../lib/AuthContext';

// Using Slack SVG icon since MUI doesn't have one
const SlackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

// ============================================================================
// Types and Schemas
// ============================================================================

/**
 * Channel types enum
 */
export const ChannelTypeEnum = z.enum(['EMAIL', 'WEBHOOK', 'SLACK', 'SMS']);
export type ChannelType = z.infer<typeof ChannelTypeEnum>;

/**
 * Webhook method enum
 */
const WebhookMethodEnum = z.enum(['POST', 'GET']);

/**
 * Email config schema
 */
const emailConfigSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

/**
 * Webhook config schema
 */
const webhookConfigSchema = z.object({
  url: z.string().url('Please enter a valid URL'),
  secret: z.string().optional(),
  method: WebhookMethodEnum.default('POST'),
});

/**
 * Slack config schema
 */
const slackConfigSchema = z.object({
  webhookUrl: z
    .string()
    .url('Please enter a valid Slack webhook URL')
    .refine((url) => url.includes('hooks.slack.com'), {
      message: 'URL must be a valid Slack webhook URL',
    }),
  channel: z.string().optional(),
});

/**
 * SMS config schema
 * Phone number must be in E.164 format (e.g., +14155551234)
 */
const smsConfigSchema = z.object({
  phoneNumber: z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Phone number must be in E.164 format (e.g., +14155551234)'),
});

/**
 * Combined form schema with type-specific validation
 */
const channelFormSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
    type: ChannelTypeEnum,
    // Email fields
    email: z.string().optional(),
    // Webhook fields
    url: z.string().optional(),
    secret: z.string().optional(),
    method: WebhookMethodEnum.optional(),
    // Slack fields
    webhookUrl: z.string().optional(),
    channel: z.string().optional(),
    // SMS fields
    phoneNumber: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    switch (data.type) {
      case 'EMAIL': {
        const result = emailConfigSchema.safeParse({ email: data.email });
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: [issue.path[0] as string],
            });
          });
        }
        break;
      }
      case 'WEBHOOK': {
        const result = webhookConfigSchema.safeParse({
          url: data.url,
          secret: data.secret,
          method: data.method || 'POST',
        });
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: [issue.path[0] as string],
            });
          });
        }
        break;
      }
      case 'SLACK': {
        const result = slackConfigSchema.safeParse({
          webhookUrl: data.webhookUrl,
          channel: data.channel,
        });
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: [issue.path[0] as string],
            });
          });
        }
        break;
      }
      case 'SMS': {
        const result = smsConfigSchema.safeParse({
          phoneNumber: data.phoneNumber,
        });
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: [issue.path[0] as string],
            });
          });
        }
        break;
      }
    }
  });

type ChannelFormData = z.infer<typeof channelFormSchema>;

/**
 * Initial data type (from API)
 */
export interface ChannelInitialData {
  id: string;
  name: string;
  type: ChannelType;
  config: {
    email?: string;
    url?: string;
    secret?: string;
    method?: 'POST' | 'GET';
    webhookUrl?: string;
    channel?: string;
    phoneNumber?: string;
  };
}

/**
 * Submit data type (for API)
 */
export interface ChannelSubmitData {
  name: string;
  type: ChannelType;
  config: {
    email?: string;
    url?: string;
    secret?: string;
    method?: 'POST' | 'GET';
    webhookUrl?: string;
    channel?: string;
    phoneNumber?: string;
  };
}

/**
 * ChannelForm component props
 */
interface ChannelFormProps {
  mode: 'create' | 'edit';
  initialData?: ChannelInitialData;
  onSubmit: (data: ChannelSubmitData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const CHANNEL_TYPES: {
  value: ChannelType;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'EMAIL',
    label: 'Email',
    description: 'Send notifications via email',
    icon: <EmailIcon />,
  },
  {
    value: 'WEBHOOK',
    label: 'Webhook',
    description: 'Send HTTP requests to your server',
    icon: <WebhookIcon />,
  },
  {
    value: 'SLACK',
    label: 'Slack',
    description: 'Post messages to a Slack channel',
    icon: <SlackIcon />,
  },
  {
    value: 'SMS',
    label: 'SMS',
    description: 'Send text messages to your phone',
    icon: <PhoneIcon />,
  },
];

const WEBHOOK_METHODS = [
  { value: 'POST', label: 'POST' },
  { value: 'GET', label: 'GET' },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get default form values
 */
function getDefaultValues(initialData?: ChannelInitialData): ChannelFormData {
  if (initialData) {
    return {
      name: initialData.name,
      type: initialData.type,
      email: initialData.config.email || '',
      url: initialData.config.url || '',
      secret: initialData.config.secret || '',
      method: initialData.config.method || 'POST',
      webhookUrl: initialData.config.webhookUrl || '',
      channel: initialData.config.channel || '',
      phoneNumber: initialData.config.phoneNumber || '',
    };
  }

  return {
    name: '',
    type: 'EMAIL',
    email: '',
    url: '',
    secret: '',
    method: 'POST',
    webhookUrl: '',
    channel: '',
    phoneNumber: '',
  };
}

// ============================================================================
// Component
// ============================================================================

/**
 * ChannelForm component
 * Reusable form for creating and editing notification channels
 */
export function ChannelForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  error,
}: ChannelFormProps) {
  const { user } = useAuth();
  const isFreePlan = !user?.plan || user.plan.toUpperCase() === 'FREE';

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<ChannelFormData>({
    resolver: zodResolver(channelFormSchema),
    defaultValues: getDefaultValues(initialData),
  });

  const channelType = watch('type');

  // Check if SMS is selected on free plan
  const showSmsUpgradePrompt = isFreePlan && channelType === 'SMS';

  const onFormSubmit = (data: ChannelFormData) => {
    // Transform form data to API format
    const submitData: ChannelSubmitData = {
      name: data.name,
      type: data.type,
      config: {},
    };

    // Build config based on type
    switch (data.type) {
      case 'EMAIL':
        if (data.email) {
          submitData.config.email = data.email;
        }
        break;
      case 'WEBHOOK':
        if (data.url) {
          submitData.config.url = data.url;
        }
        submitData.config.method = data.method || 'POST';
        if (data.secret) {
          submitData.config.secret = data.secret;
        }
        break;
      case 'SLACK':
        if (data.webhookUrl) {
          submitData.config.webhookUrl = data.webhookUrl;
        }
        if (data.channel) {
          submitData.config.channel = data.channel;
        }
        break;
      case 'SMS':
        if (data.phoneNumber) {
          submitData.config.phoneNumber = data.phoneNumber;
        }
        break;
    }

    onSubmit(submitData);
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 600 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={3}>
          {/* Channel Name */}
          <Grid item xs={12}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Channel Name"
                  placeholder="My notification channel"
                  error={!!errors.name}
                  helperText={errors.name?.message || 'A friendly name to identify this channel'}
                  disabled={isLoading}
                />
              )}
            />
          </Grid>

          {/* Channel Type */}
          <Grid item xs={12}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Channel Type"
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  disabled={isLoading || mode === 'edit'}
                >
                  {CHANNEL_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{type.icon}</Box>
                        <Box>
                          <Typography variant="body2">
                            {type.label}
                            {isFreePlan && type.value === 'SMS' && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  ml: 1,
                                  px: 0.75,
                                  py: 0.25,
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  borderRadius: 1,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                }}
                              >
                                PRO
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {type.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {/* Upgrade prompt for SMS on free plan */}
            {showSmsUpgradePrompt && (
              <UpgradePrompt
                message="SMS notifications are available on the Pro plan."
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          {/* Email-specific fields */}
          {channelType === 'EMAIL' && (
            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email Address"
                    placeholder="alerts@example.com"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message || 'Notifications will be sent to this email'}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}

          {/* Webhook-specific fields */}
          {channelType === 'WEBHOOK' && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="url"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Webhook URL"
                      placeholder="https://your-server.com/webhook"
                      error={!!errors.url}
                      helperText={errors.url?.message || 'The URL to send notifications to'}
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="method"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="HTTP Method"
                      error={!!errors.method}
                      helperText={errors.method?.message}
                      disabled={isLoading}
                    >
                      {WEBHOOK_METHODS.map((method) => (
                        <MenuItem key={method.value} value={method.value}>
                          {method.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="secret"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Secret (Optional)"
                      placeholder="webhook-secret"
                      error={!!errors.secret}
                      helperText={errors.secret?.message || 'Used to sign webhook requests'}
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <KeyIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {/* Slack-specific fields */}
          {channelType === 'SLACK' && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="webhookUrl"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Slack Webhook URL"
                      placeholder="https://hooks.slack.com/services/..."
                      error={!!errors.webhookUrl}
                      helperText={
                        errors.webhookUrl?.message ||
                        'Get this from Slack App settings > Incoming Webhooks'
                      }
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                              <SlackIcon />
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="channel"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Channel Override (Optional)"
                      placeholder="alerts"
                      error={!!errors.channel}
                      helperText={
                        errors.channel?.message || 'Override the default channel (without the #)'
                      }
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TagIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {/* SMS-specific fields */}
          {channelType === 'SMS' && (
            <Grid item xs={12}>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    placeholder="+14155551234"
                    error={!!errors.phoneNumber}
                    helperText={
                      errors.phoneNumber?.message ||
                      'International format with country code (e.g., +1 for US)'
                    }
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isLoading || (!isDirty && mode === 'edit') || showSmsUpgradePrompt}
          >
            {isLoading
              ? mode === 'create'
                ? 'Creating...'
                : 'Saving...'
              : mode === 'create'
                ? 'Create Channel'
                : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default ChannelForm;
